import React, { useState, useEffect } from 'react';
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  BarElement, 
  LineElement,
  PointElement,
  Title, 
  Tooltip, 
  Legend 
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import axios from 'axios';
import '../styles/SalesCharts.css';
import ExpenseDoughnutChart from './ExpenseDoughnutChart';

// Registrar componentes de Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const SalesCharts = ({ selectedPOS }) => {
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchChartData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const user = JSON.parse(localStorage.getItem('user'));
        const userRole = user?.role;
        
        console.log('DEBUG - Token disponible:', !!token);
        console.log('DEBUG - Información de usuario:', user);
        console.log('DEBUG - Rol del usuario:', userRole);
        console.log('DEBUG - selectedPOS:', selectedPOS);
        
        // Obtener el rol directamente del localStorage para asegurar que estamos usando el valor correcto
        const storedUser = localStorage.getItem('user');
        console.log('DEBUG - Usuario almacenado en localStorage (raw):', storedUser);
        
        let parsedUser;
        try {
          parsedUser = JSON.parse(storedUser);
          console.log('DEBUG - Usuario parseado correctamente:', parsedUser);
        } catch (e) {
          console.error('DEBUG - Error al parsear usuario:', e);
        }
        
        const actualRole = parsedUser?.role;
        console.log('DEBUG - Rol actual del usuario:', actualRole);
        
        let url;
        // Usar endpoint diferente según el rol del usuario
        // Forzar el uso del endpoint para vendedores si el rol es 'seller'
        if (actualRole === 'seller') {
          url = '/api/seller-stats/daily-sales-chart';
          console.log('DEBUG - Usando endpoint para vendedores:', url);
        } else {
          url = '/api/stats/monthly-chart';
          if (selectedPOS && selectedPOS !== 'all') {
            url += `?pointOfSaleId=${selectedPOS}`;
          }
          console.log('DEBUG - Usando endpoint para admin:', url);
        }
        
        const response = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        setChartData(response.data);
        setError(null);
      } catch (error) {
        console.error('Error fetching chart data:', error);
        
        // Crear datos vacíos para el mes actual
        const now = new Date();
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth() + 1;
        const lastDay = new Date(currentYear, currentMonth, 0).getDate();
        
        // Crear etiquetas para cada día del mes
        const labels = Array.from({ length: lastDay }, (_, i) => `Día ${i + 1}`);
        
        // Crear arrays de datos vacíos
        const dailyTotals = Array(lastDay).fill(0);
        const cumulativeTotals = Array(lastDay).fill(0);
        
        // Crear datos de gráfica vacíos
        const emptyChartData = {
          labels,
          year: currentYear,
          month: currentMonth,
          monthlyData: {
            totals: dailyTotals
          },
          cumulativeData: {
            totals: cumulativeTotals
          }
        };
        
        // Establecer datos vacíos pero no mostrar mensaje de error
        setChartData(emptyChartData);
        setError(null); // No mostrar mensaje de error
      } finally {
        setLoading(false);
      }
    };

    fetchChartData();
  }, [selectedPOS]);

  if (loading) {
    return <div className="charts-loading">Cargando gráficas...</div>;
  }

  // Eliminamos la verificación de error para que no se muestre el mensaje
  // Sólo verificamos si no hay datos de gráficas
  if (!chartData) {
    // Crear un espacio vacío en lugar de mostrar mensaje
    return <div className="charts-empty"></div>;
  }

  // Obtener el nombre del mes actual
  const getMonthName = (monthNumber) => {
    const months = [
      'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];
    return months[monthNumber - 1];
  };

  const monthName = getMonthName(chartData.month);
  const userRole = JSON.parse(localStorage.getItem('user'))?.role;
  
  // Configuración para la gráfica de barras (ventas diarias/mensuales)
  const barChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: userRole === 'seller' ? 
          `Ventas Diarias de ${monthName} ${chartData.year} (Mis Ventas)` : 
          `Ventas Mensuales ${chartData.year}`,
      },
    },
  };

  const barChartData = {
    labels: chartData.labels,
    datasets: [
      {
        label: userRole === 'seller' ? 'Ventas Diarias' : 'Ventas Mensuales',
        data: chartData.monthlyData.totals,
        backgroundColor: 'rgba(53, 162, 235, 0.5)',
      },
    ],
  };

  // Configuración para la gráfica de línea (ventas acumuladas)
  const lineChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: userRole === 'seller' ? 
          `Ventas Acumuladas de ${monthName} ${chartData.year} (Mis Ventas)` : 
          `Ventas Acumuladas ${chartData.year}`,
      },
    },
  };

  const lineChartData = {
    labels: chartData.labels,
    datasets: [
      {
        label: userRole === 'seller' ? 'Ventas Acumuladas del Mes' : 'Ventas Acumuladas',
        data: chartData.cumulativeData.totals,
        borderColor: 'rgb(255, 99, 132)',
        backgroundColor: 'rgba(255, 99, 132, 0.5)',
        tension: 0.1,
      },
    ],
  };

  return (
    <div className="sales-charts-container">
      <div className="chart-wrapper">
        <h3>Ventas Mensuales</h3>
        <div className="chart-container">
          <Bar options={barChartOptions} data={barChartData} />
        </div>
      </div>
      
      {/* Gráfica de Gastos Mensuales por Categoría */}
      <div className="chart-wrapper expense-chart-wrapper">
        <h3>Gastos Mensuales por Categoría</h3>
        <div className="chart-container">
          <ExpenseDoughnutChart selectedPOS={selectedPOS} />
        </div>
      </div>
      
      <div className="chart-wrapper">
        <h3>Ventas Acumuladas</h3>
        <div className="chart-container">
          <Line options={lineChartOptions} data={lineChartData} />
        </div>
      </div>
    </div>
  );
};

export default SalesCharts;
